import type {AreaDispatcher} from '@gaia/hooks/useAreas';
import type {TrackDispatcher} from '@gaia/hooks/useTracks';
import type {WaypointDispatcher} from '@gaia/hooks/useWaypoints';
import type {ImportState, SharedData, UnitSystem} from '@gaia/types';
import type {LngLatBounds, MapLayerMouseEvent, MapMouseEvent, MapTouchEvent} from 'mapbox-gl';
import {useCallback, useMemo, useRef} from 'react';

import {editingArea} from './types/editingArea';
import {editingFolder} from './types/editingFolder';
import {editingRoute} from './types/editingRoute';
import {editingWaypoint} from './types/editingWaypoint';
import {embedded} from './types/embedded';
import {normal} from './types/normal';

export interface UseInteractionHandlersOptions<T = InteractionState> {
    sharedData: SharedData;
    interaction: T;
    map: MapboxMap | undefined;
    mapboxStyle: GaiaMapboxStyle | undefined;
    onClickExternal?: (event: MapLayerMouseEvent) => void;
    onDblClickExternal?: (event: MapLayerMouseEvent) => void;
    onInteractionChange: SetState<InteractionState>;
    onMouseDownExternal?: (event: MapLayerMouseEvent) => void;
    onMouseMoveExternal?: (event: MapLayerMouseEvent) => void;
    onMouseOutExternal?: (event: MapLayerMouseEvent) => void;
    onMouseUpExternal?: (event: MapLayerMouseEvent) => void;
    onOverlaysChange: SetState<OverlaysState>;
    setSelections: SetState<SelectionState[]>;
    onTouchEndExternal?: (event: MapTouchEvent) => void;
    onTouchMoveExternal?: (event: MapTouchEvent) => void;
    onTouchStartExternal?: (event: MapTouchEvent) => void;
    overlays: OverlaysState;
    selections: SelectionState[];
    trackDispatcher: TrackDispatcher;
    tracks: {[localId: string]: Track};
    waypointDispatcher: WaypointDispatcher;
    waypoints: {[localId: string]: Waypoint};
    areaDispatcher: AreaDispatcher;
    areas: {[areaLocalId: string]: Area};
    project: MapProject;
    sidebar: SidebarState;
    onSidebarChange: SetState<SidebarState>;
    bounds: LngLatBounds | undefined;
    isAuthenticated: boolean;
    unitSystem: UnitSystem;
    importState: ImportState;
    setImportState: SetState<ImportState>;
    querySourceFeatures: MapQuerySourceFeatures;
    isEmbedded?: boolean;
    controlsExpanded?: boolean;
}

type Handler = (
    event: MapTouchEvent | MapMouseEvent,
    options: UseInteractionHandlersOptions<any>
) => void;

type Handlers = Record<
    InteractionState['type'],
    {
        getCursor: (interaction: any) => MapCursor;
        onMouseDown: Handler;
        onMouseUp: Handler;
        onMouseMove: Handler;
        onMouseOut: Handler;
        onClick: Handler;
        onDblClick: Handler;
    }
>;

export type MapCursor = 'default' | 'pointer' | 'grab' | 'grabbing' | 'crosshair' | 'not-allowed';

export type InteractionHandlers = ReturnType<typeof useInteractionHandlers>['interactionHandlers'];

export function useInteractionHandlers(options: UseInteractionHandlersOptions) {
    const handlers = useMemo<Handlers>(
        () => ({
            normal: options.isEmbedded ? embedded : normal,
            editingRoute,
            editingWaypoint,
            editingArea,
            editingFolder,
        }),
        [options.isEmbedded]
    );

    const optionsRef = useRef(options);
    optionsRef.current = options;

    const latestCursor = useRef<MapCursor>('default');
    latestCursor.current = handlers[options.interaction.type].getCursor(options.interaction);

    const onClick = useMemo(() => {
        return ignoreDblClick((event: MapLayerMouseEvent) => {
            const {current: currentOptions} = optionsRef;
            const {onClickExternal, interaction} = currentOptions;

            onClickExternal?.(event);
            handlers[interaction.type].onClick(event, currentOptions);
        });
    }, [handlers]);

    const onDblClick = useCallback(
        (event: MapLayerMouseEvent) => {
            const {current: currentOptions} = optionsRef;
            const {onDblClickExternal, interaction} = currentOptions;

            onDblClickExternal?.(event);
            handlers[interaction.type].onDblClick(event, currentOptions);
        },
        [handlers]
    );

    const onMouseMove = useCallback(
        (event: MapLayerMouseEvent) => {
            const {current: currentOptions} = optionsRef;
            const {onMouseMoveExternal, interaction} = currentOptions;

            onMouseMoveExternal?.(event);
            handlers[interaction.type].onMouseMove(event, currentOptions);
        },
        [handlers]
    );

    const onMouseDown = useCallback(
        (event: MapLayerMouseEvent) => {
            const {current: currentOptions} = optionsRef;
            const {onMouseDownExternal, interaction} = currentOptions;

            onMouseDownExternal?.(event);
            handlers[interaction.type].onMouseDown(event, currentOptions);
        },
        [handlers]
    );

    const onMouseUp = useCallback(
        (event: MapLayerMouseEvent) => {
            const {current: currentOptions} = optionsRef;
            const {onMouseUpExternal, interaction} = currentOptions;

            onMouseUpExternal?.(event);
            handlers[interaction.type].onMouseUp(event, currentOptions);
        },
        [handlers]
    );

    const onMouseOut = useCallback(
        (event: MapLayerMouseEvent) => {
            const {current: currentOptions} = optionsRef;
            const {onMouseOutExternal, interaction} = currentOptions;

            onMouseOutExternal?.(event);
            handlers[interaction.type].onMouseOut(event, currentOptions);
        },
        [handlers]
    );

    const onTouchStart = useCallback(
        (event: MapTouchEvent) => {
            const {current: currentOptions} = optionsRef;
            const {onTouchStartExternal, interaction} = currentOptions;

            onTouchStartExternal?.(event);
            handlers[interaction.type].onMouseDown(event, currentOptions);
        },
        [handlers]
    );

    const onTouchEnd = useCallback(
        (event: MapTouchEvent) => {
            const {current: currentOptions} = optionsRef;
            const {onTouchEndExternal, interaction} = currentOptions;

            onTouchEndExternal?.(event);
            handlers[interaction.type].onMouseUp(event, currentOptions);
        },
        [handlers]
    );

    const onTouchMove = useCallback(
        (event: MapTouchEvent) => {
            const {current: currentOptions} = optionsRef;
            const {onTouchMoveExternal, interaction} = currentOptions;

            onTouchMoveExternal?.(event);
            handlers[interaction.type].onMouseMove(event, currentOptions);
        },
        [handlers]
    );

    return useMemo(
        () => ({
            cursor: latestCursor,
            interactionHandlers: {
                onClick,
                onDblClick,
                onMouseMove,
                onMouseDown,
                onMouseUp,
                onMouseOut,
                onTouchStart,
                onTouchEnd,
                onTouchMove,
            },
        }),
        [
            onClick,
            onDblClick,
            onMouseDown,
            onMouseMove,
            onMouseOut,
            onMouseUp,
            onTouchEnd,
            onTouchMove,
            onTouchStart,
        ]
    );
}

/**
 * We want to allow both single-click (select a map feature) and double-click (zoom in) mouse
 * interactions. However, if the user double-clicks, we don't want to also trigger a single-
 * click. The solution is to introduce a short delay after the first click: if another click
 * occurs within that delay, we ignore the first click and treat it as part of the double-click.
 */
function ignoreDblClick(handler: (event: MapLayerMouseEvent) => void) {
    const DBL_CLICK_INTERVAL = 250;
    let id: ReturnType<typeof setTimeout> | undefined;

    return (event: MapLayerMouseEvent) => {
        // https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/detail
        if (event.originalEvent.detail !== 1) {
            clearTimeout(id);
        } else {
            id = setTimeout(() => {
                handler(event);
            }, DBL_CLICK_INTERVAL);
        }
    };
}
